/**
 * 非分页列表数据 Hook
 *
 * 用于加载不需要分页的简单列表数据，如下拉选项、标签列表、分类列表等。
 * 与 useTableData（分页列表）互补。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiResponse } from '../types/api';
import { isApiSuccess } from '../utils/api-helpers';
import {
  handleAbortableRequestError,
  handleApiFailure,
  runAbortableTask,
  startAbortableRequest,
} from './shared-async';
import { useLatestRef } from './useLatestRef';

function buildParamsKey(value: unknown): string {
  if (value === undefined) return '__undefined__';
  if (value === null) return '__null__';

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * useListData 配置
 */
export interface UseListDataOptions<T, P = void> {
  /** 列表数据请求 API */
  fetchApi: (params?: P) => Promise<ApiResponse<T[]>>;
  /** 请求参数（变化时自动重新请求） */
  params?: P;
  /** 是否在挂载时自动请求，默认 true */
  autoLoad?: boolean;
  /** 成功状态码，默认 '200' */
  successCode?: string | number;
  /** 数据转换 */
  transformData?: (list: T[]) => T[];
  /** 请求成功回调 */
  onSuccess?: (list: T[]) => void;
  /** 请求失败回调 */
  onError?: (error: Error) => void;
}

/**
 * useListData 返回值
 */
export interface UseListDataReturn<T> {
  /** 列表数据 */
  data: T[];
  /** 加载中状态 */
  loading: boolean;
  /** 错误信息 */
  error: Error | null;
  /** 手动刷新 */
  refresh: () => Promise<void>;
  /** 手动设置数据 */
  setData: React.Dispatch<React.SetStateAction<T[]>>;
}

/**
 * 非分页列表数据 Hook
 *
 * @example
 * ```tsx
 * // 基础用法：下拉选项
 * const { data: categories, loading } = useListData({
 *   fetchApi: getCategoryList,
 * });
 *
 * // 带参数：按条件加载
 * const { data: tags, refresh } = useListData({
 *   fetchApi: (params) => getTagList(params),
 *   params: { type: 'article' },
 * });
 *
 * // 数据转换：转为 Select options
 * const { data: options } = useListData({
 *   fetchApi: getRoleList,
 *   transformData: (list) => list.map(r => ({ label: r.name, value: r.id })),
 * });
 * ```
 */
export function useListData<T, P = void>(
  options: UseListDataOptions<T, P>,
): UseListDataReturn<T> {
  const { fetchApi, params, autoLoad = true, successCode = '200' } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // AbortController 引用，用于取消进行中的请求
  const abortControllerRef = useRef<AbortController | null>(null);

  // 用 useLatestRef 稳定回调和参数引用，防止 inline 函数/对象导致无限重请求
  const transformDataRef = useLatestRef(options.transformData);
  const onSuccessRef = useLatestRef(options.onSuccess);
  const onErrorRef = useLatestRef(options.onError);
  const paramsRef = useLatestRef(params);
  const paramsKey = useMemo(() => buildParamsKey(params), [params]);
  const lastAutoLoadRef = useRef(autoLoad);
  const lastLoadedParamsKeyRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    const controller = startAbortableRequest(abortControllerRef);

    setLoading(true);
    setError(null);

    await runAbortableTask({
      controller,
      setLoading,
      execute: async () => {
        const response = await fetchApi(paramsRef.current as P);

        // 请求已被取消，忽略结果
        if (controller.signal.aborted) return;

        const isSuccess = isApiSuccess(response, successCode);

        if (isSuccess && response.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          const result = transformDataRef.current
            ? transformDataRef.current(list)
            : list;
          setData(result);
          onSuccessRef.current?.(result);
        } else {
          handleApiFailure({
            message: response.msg || response.message || response.errorMessage,
            fallbackMessage: 'Failed to fetch list',
            setError,
            onError: onErrorRef.current,
          });
        }
      },
      onError: (error) =>
        handleAbortableRequestError({
          error,
          controller,
          setError,
          onError: onErrorRef.current,
        }),
    });
  }, [
    fetchApi,
    successCode,
    onErrorRef,
    onSuccessRef,
    paramsRef,
    transformDataRef,
  ]);

  useEffect(() => {
    const autoLoadEnabled = autoLoad && !lastAutoLoadRef.current;
    lastAutoLoadRef.current = autoLoad;

    const shouldFetch =
      autoLoad &&
      (autoLoadEnabled || lastLoadedParamsKeyRef.current !== paramsKey);

    if (shouldFetch) {
      lastLoadedParamsKeyRef.current = paramsKey;
      fetchData();
    }
    // 组件卸载或参数变化时取消进行中的请求
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [autoLoad, fetchData, paramsKey]);

  return { data, loading, error, refresh: fetchData, setData };
}
